import axios, { AxiosInstance, AxiosResponse, AxiosError } from "axios";
import { showToast } from "../store/uiSlice";

// Lazy load store to avoid circular dependency
let storeInstance: any = null;
const getStore = () => {
  if (!storeInstance) {
    storeInstance = require("../store").store;
  }
  return storeInstance;
};

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:4000/api";

// Static assets (e.g. uploaded avatars) are served directly by Express at
// the backend's origin, not under the /api prefix Nest's controllers use -
// so this strips the trailing /api to get a base for building those URLs.
export const API_ORIGIN = API_BASE_URL.replace(/\/api\/?$/, "");

// Prefixes a server-relative asset path (e.g. "/avatars/xxx.png") with the
// backend's origin. Leaves absolute URLs (http://..., data:) untouched.
export const resolveAssetUrl = (assetPath?: string | null): string | undefined => {
  if (!assetPath) return undefined;
  if (/^(https?:)?\/\//.test(assetPath) || assetPath.startsWith("data:")) {
    return assetPath;
  }
  return `${API_ORIGIN}${assetPath}`;
};

// Create axios instance with default config
const axiosClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: false, // Set to true if backend uses cookies
});

// Request interceptor - Add auth token to requests
axiosClient.interceptors.request.use(
  (config: any) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors globally
axiosClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError<any>) => {
    // Check if this is an endpoint that handles its own errors - don't show toast
    const isAuthEndpoint = error.config?.url?.includes('/auth/login') ||
                            error.config?.url?.includes('/auth/signup') ||
                            error.config?.url?.includes('/auth/google') ||
                            error.config?.url?.includes('/auth/facebook');

    // Task creation/update endpoints handle their own validation errors
    const isTaskEndpoint = error.config?.url?.includes('/tasks') &&
                           (error.config?.method === 'post' || error.config?.method === 'patch');

    // Board/workspace creation endpoints handle their own validation errors
    const isFormEndpoint = (error.config?.url?.includes('/boards') ||
                            error.config?.url?.includes('/workspaces')) &&
                           error.config?.method === 'post';

    const shouldSkipToast = isAuthEndpoint || isTaskEndpoint || isFormEndpoint;

    if (error.response) {
      // Handle specific error codes
      switch (error.response.status) {
        case 401:
          // Unauthorized - only show toast for authenticated requests, not login attempts
          if (!shouldSkipToast) {
            const errorMessage = error.response.data?.message || "Session expired. Please login again.";
            getStore().dispatch(showToast({
              message: errorMessage,
              type: "error"
            }));
            // Clear token if it exists (for expired sessions, but not during login)
            localStorage.removeItem("authToken");
          }
          // DO NOT redirect - let the login form handle the error
          break;
        case 403:
          // Forbidden
          if (!shouldSkipToast) {
            getStore().dispatch(showToast({
              message: "Access forbidden. You don't have permission to access this resource.",
              type: "error"
            }));
          }
          break;
        case 404:
          // Not found
          if (!shouldSkipToast) {
            getStore().dispatch(showToast({
              message: "Resource not found.",
              type: "error"
            }));
          }
          break;
        case 500:
          // Server error
          if (!shouldSkipToast) {
            getStore().dispatch(showToast({
              message: "Server error. Please try again later.",
              type: "error"
            }));
          }
          break;
        default:
          if (!shouldSkipToast) {
            const defaultMessage = error.response.data?.message || "An error occurred. Please try again.";
            getStore().dispatch(showToast({
              message: defaultMessage,
              type: "error"
            }));
          }
      }
    } else if (error.request) {
      // Request made but no response received
      if (!shouldSkipToast) {
        getStore().dispatch(showToast({
          message: "No response from server. Please check your connection.",
          type: "error"
        }));
      }
    } else {
      // Error in request setup
      if (!shouldSkipToast) {
        getStore().dispatch(showToast({
          message: error.message || "An error occurred.",
          type: "error"
        }));
      }
    }
    return Promise.reject(error);
  }
);

export default axiosClient;
